/**
 * UTM capture & attribution helper.
 *
 * Two responsibilities:
 *   1. On every navigation, parse incoming utm_* query params and persist them
 *      to localStorage so we can stitch them onto the user row at sign-up.
 *      First-touch wins — once we've seen a UTM payload, we don't overwrite
 *      with a later non-UTM landing.
 *   2. Anonymous beacon to /api/utm so the marketing dashboard can show
 *      visitor-level funnel data even before the visitor signs in (or never
 *      does).
 *
 * Cookie id: random 32-char string, generated lazily on first call, kept in
 * localStorage. NOT a tracking ID across devices — purely per-browser glue
 * between visit-time and signup-time.
 */
import { analyticsAllowed } from './consent';

const COOKIE_KEY = 'roamzy_cookie_id';
const UTM_KEY = 'roamzy_utm';
const UTM_PARAMS = ['source', 'medium', 'campaign', 'content', 'term'] as const;
type UtmParam = typeof UTM_PARAMS[number];

export interface CapturedUtm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
}
interface StoredUtm {
  utm: CapturedUtm;
  landingPage: string;
  referrer: string;
  ts: number;
}

/** Get-or-generate the per-browser cookie id. */
export function getCookieId(): string {
  try {
    let id = localStorage.getItem(COOKIE_KEY);
    if (!id) {
      // crypto.randomUUID() is widely supported (Safari 15.4+, all modern). Fallback below.
      const fallback = (): string =>
        `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`.padEnd(24, '0');
      id = (crypto.randomUUID?.() ?? '').replace(/-/g, '') || fallback();
      localStorage.setItem(COOKIE_KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

function readStoredUtm(): StoredUtm | null {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredUtm;
    if (!parsed || typeof parsed !== 'object' || !parsed.utm) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredUtm(s: StoredUtm): void {
  try { localStorage.setItem(UTM_KEY, JSON.stringify(s)); } catch { /* best-effort */ }
}

/**
 * Parse query-string for utm_* params. Returns the captured object only if
 * at least one parameter is present.
 */
function parseQueryUtm(search: string): CapturedUtm | null {
  if (!search) return null;
  const qs = new URLSearchParams(search);
  const out: CapturedUtm = {};
  let any = false;
  for (const k of UTM_PARAMS) {
    const v = qs.get(`utm_${k}`);
    if (v) { out[k as UtmParam] = v.slice(0, 128); any = true; }
  }
  return any ? out : null;
}

/**
 * Called by the router on every route resolve. If the URL carries fresh
 * utm_* params, store them (first-touch wins) and beacon the visit to
 * /api/utm. Idempotent — safe to call on every navigation.
 *
 * Backend dedupes same (cookieId, campaign) within 24h, so even repeated
 * navigations during a single session don't bloat the table.
 */
export function captureUtm(): void {
  const fresh = parseQueryUtm(window.location.search);
  const stored = readStoredUtm();
  // First-touch attribution: only persist if we don't already have a stored
  // UTM, OR the existing one is older than 30 days (campaign refresh window).
  const STALE_MS = 30 * 24 * 60 * 60 * 1000;
  const shouldStore = fresh && (!stored || Date.now() - stored.ts > STALE_MS);
  if (shouldStore && fresh) {
    writeStoredUtm({
      utm: fresh,
      landingPage: window.location.pathname + window.location.search,
      referrer: document.referrer || '',
      ts: Date.now(),
    });
  }

  // Beacon — fire-and-forget. Send only if we have UTM OR a referrer.
  const haveSomething = fresh || (document.referrer && document.referrer !== window.location.href);
  if (!haveSomething) return;
  // Same consent gate as analytics: this beacon carries the permanent cookieId
  // and the backend stores it alongside the IP and user-agent (utm_visits).
  // Attribution itself still lands in storage, so a visitor who declines is
  // attributed correctly IF they later accept. Audit 2026-07-29.
  if (!analyticsAllowed()) return;
  const cookieId = getCookieId();
  const body = JSON.stringify({
    cookieId,
    utm: fresh ?? {},
    landingPage: window.location.pathname + window.location.search,
    referrer: document.referrer || '',
  });
  try {
    // keepalive lets the request survive a same-tap navigation.
    fetch('/api/utm', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => { /* best-effort */ });
  } catch {
    /* swallow */
  }
}

/**
 * Returns the cached UTM payload + the visitor's cookieId to spread into
 * the auth POST body. Backend uses:
 *   - utm/landingPage  → first-touch attribution on user row
 *   - cookieId         → A/B experiment event tracking (signup metric)
 *
 * cookieId is always present (lazily generated). UTM only if we caught
 * a campaign visit. Caller can spread the whole object unconditionally.
 */
export function authPayloadExtras(): Record<string, unknown> {
  const stored = readStoredUtm();
  const out: Record<string, unknown> = { cookieId: getCookieId() };
  if (stored) {
    out.utm = stored.utm;
    out.landingPage = stored.landingPage;
  }
  return out;
}
